import { Link, useRouteMatch } from 'react-router-dom';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Box } from '@givebutter/primitives/box';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal } from '@givebutter/util';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentPlanOrById } from 'customHooks/useCurrentPlanOrById';
import { useInfiniteTransactions } from 'customHooks/useInfiniteTransactions';
import { rawDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { IndexedTransaction } from 'types/domain';
import { PaginatedData } from 'types/pagination';

const PAGINATED_PER_PAGE = 5;

const PastRecurringPlanTransactions = () => {
    const { accountId, timezone } = useCurrentAccount();
    const match = useRouteMatch();
    const queryClient = useQueryClient();
    const currencySymbol = useCurrencySymbol();
    const { data: plan } = useCurrentPlanOrById();
    const planId = plan.id;
    const params = {
        plan_id: planId,
        per_page: PAGINATED_PER_PAGE,
    };
    const { ref, data } = useInfiniteTransactions(params);
    const previousTransactions = data.pages;
    const totalPreviousTransactions = queryClient.getQueryData<
        InfiniteData<PaginatedData<IndexedTransaction>>
    >(transactionQueryKeys.transactionsWithFilters(accountId, params))?.pages[0].meta?.total;
    const noPreviousTransactions = totalPreviousTransactions === 0;

    return (
        <div className={'flex flex-col gap-6'}>
            <div className={'flex items-center gap-2.5'}>
                <p className={'text-2xl font-medium'}>Past transactions</p>
                <Separator className={'!h-6'} orientation={'vertical'} />
                <p className={'mt-1 text-sm text-gray-500'}>{totalPreviousTransactions}</p>
            </div>
            {noPreviousTransactions && <p className={'text-gray-600'}>No previous transactions</p>}
            {previousTransactions.map(transaction => (
                <Box key={transaction.id} className={'flex gap-4 p-4'}>
                    <Avatar.Root>
                        <Avatar.Fallback
                            className={'border border-green-500 bg-green-100'}
                            aria-label="Check icon"
                        >
                            <FontAwesomeIcon className={'text-green-500'} icon={faCheck} />
                        </Avatar.Fallback>
                    </Avatar.Root>
                    <div className={'flex flex-grow items-center justify-between gap-4'}>
                        <div className={'flex flex-col'}>
                            <p className={'text-sm font-medium'}>
                                {currencySymbol}
                                {maskDecimal(transaction.amount)}
                            </p>
                            <Link
                                className={'text-sm font-medium'}
                                to={`${match.url}/transactions/${transaction.id}`}
                            >
                                View transaction
                            </Link>
                        </div>
                        <p className={'text-sm text-gray-500'}>
                            {rawDateFromServer(transaction.transacted_at)
                                .setZone(timezone)
                                .toLocaleString(DateTime.DATE_SHORT)}
                        </p>
                    </div>
                </Box>
            ))}
            <div ref={ref} />
        </div>
    );
};

export { PastRecurringPlanTransactions };
